import { acquireToken } from '@ms/yammer-data/dist/authentication';
import { isFeatureRolloutEnabled } from '@ms/yammer-data/dist/domains/featureRollouts/selectors';
import { FeatureRollout, Store } from '@ms/yammer-data/dist/state/types';
import { RelayAuthContextProvider } from '@ms/yammer-relay/dist/environment';
import { PageSectionRefsProvider } from '@ms/yammer-web-components/dist/app/PageSectionRefs';
import { ReduxTelemetryContextProvider } from '@ms/yammer-web-components/dist/telemetry';
import { ReduxTranslationsContextProvider } from '@ms/yammer-web-components/dist/translations';
import { AnnouncerProvider } from '@ms/yammer-web-core-ui/dist/components/Announcer';
import React from 'react';
import { render } from 'react-dom';
import { HelmetProvider } from 'react-helmet-async';
import { Provider } from 'react-redux';
import { BrowserRouter } from 'react-router-dom';
import { Persistor } from 'redux-persist';
import { PersistGate } from 'redux-persist/integration/react';

import App from '../../../components/App';
import { YammerWebState } from '../../../state/types';
import config from '../../config';

import { getBasename } from './getBasename';
import { getErrorComponent } from './getErrorComponent';

export interface RendererOptions {
  readonly store: Store<YammerWebState>;
  readonly target: HTMLElement;
  readonly persistor?: Persistor;
  readonly isFailure?: boolean;
}

export type Renderer = (options: RendererOptions) => void;

const renderer: Renderer = ({ store, target, persistor, isFailure = false }) => {
  const children = isFailure ? getErrorComponent(store) : <App />;

  const basename = getBasename(store);

  const content = (
    <HelmetProvider>
      <BrowserRouter basename={basename}>
        <PageSectionRefsProvider>
          <AnnouncerProvider>{children}</AnnouncerProvider>
        </PageSectionRefsProvider>
      </BrowserRouter>
    </HelmetProvider>
  );

  const contentWithOptionalPersistGate = persistor ? (
    <PersistGate loading={null} persistor={persistor}>
      {content}
    </PersistGate>
  ) : (
    content
  );

  const authContext = {
    acquireToken: () => acquireToken([config.adal.resources.yammer]),
    host: config.hosts.graphql,
    isFeatureEnabled: (featureName: FeatureRollout) => isFeatureRolloutEnabled(store.getState(), featureName),
  };

  render(
    <Provider store={store}>
      <ReduxTranslationsContextProvider>
        <ReduxTelemetryContextProvider>
          <RelayAuthContextProvider value={authContext}>{contentWithOptionalPersistGate}</RelayAuthContextProvider>
        </ReduxTelemetryContextProvider>
      </ReduxTranslationsContextProvider>
    </Provider>,
    target
  );
};

export default renderer;
